import { useTranslation } from "react-i18next";
import { formatNumber } from "../../lib/format";

const JEDNA = { minimumFractionDigits: 1, maximumFractionDigits: 1 } as const;

export interface PromenaProps {
  sada: number | null | undefined;
  pre: number | null | undefined;
  /**
   * Da li je veći broj bolji. Za količinu, vrednost i RUC jeste; za dospelo potraživanje nije,
   * pa rast tamo ne sme da bude zelen.
   */
  viseJeBolje?: boolean;
  /** Prigušena varijanta za ćeliju tabele, gde čip ne sme da nadjača sam broj. */
  sitno?: boolean;
  locale?: string;
  /**
   * Da li odnos uopšte nešto znači. `false` ga ućutkuje iako su obe vrednosti tu.
   *
   * Nepotpun period je takav slučaj: septembar 2026 nosi 22 dana a poredi se sa punim mesecom, pa
   * su oba broja stvarna ali rast ili pad iz njih ne sledi. Čip sam računa promenu, pa ga server ne
   * može ućutkati nuliranjem indeksa — mora da mu se kaže.
   */
  tumaci?: boolean;
}

/**
 * Smer prema istom periodu prošle godine, kao čip.

 * <p>Jedno mesto za oba prikaza — pločicu i ćeliju tabele. Dok su bila dva, pločica je pisala
 * „▲ 30,9 %" a tabela go odnos „1,31": ista veličina, dva zapisa, i čitalac koji mora da zna oba.
 *
 * <p>Smer nosi i OBLIK, ne samo boju: ▲ i ▼ stoje uz procenat, jer se strana štampa i fotokopira,
 * a crveno i zeleno ne razlikuju svi.
 *
 * <p>Kad prošle godine nema šta da se poredi, čipa NEMA. Nula bi tvrdila da se nije promenilo
 * ništa, a nepromenjeno i neporedivo nisu ista stvar.
 */
export function Promena({ sada, pre, viseJeBolje = true, sitno, locale, tumaci }: PromenaProps) {
  const { t } = useTranslation();
  // NEPOTPUN PERIOD NE DOBIJA TUMAČENJE.
  // Čip sam računa promenu iz dve vrednosti, pa ga server ne može ućutkati nuliranjem indeksa —
  // zato pozivalac mora da kaže kad odnos ne znači ništa. Septembar 2026 nosi 22 dana a poredi se
  // sa punim mesecom: oba broja su stvarna, ali zelena strelica uz njih je tvrdnja koju podatak ne
  // podnosi. Oznaka u rečenici i strelica u boji su ista tvrdnja dva puta, a boja pobeđuje.
  if (tumaci === false) return null;

  const ima = pre != null && Number.isFinite(pre) && pre !== 0 && sada != null && Number.isFinite(sada);
  if (!ima) return null;

  const promena = ((sada as number) / (pre as number) - 1) * 100;
  const raste = promena > 0;
  const pada = promena < 0;
  const dobro = raste === viseJeBolje;

  return (
    <span
      className={`tag ${promena === 0 ? "neutral" : dobro ? "ok" : "err"}`}
      style={sitno ? { fontSize: 11, padding: "1px 6px" } : undefined}
      title={t("report.kpi.vsLastYear", "prema istom periodu prošle godine")}
    >
      {raste ? "▲" : pada ? "▼" : "="} {formatNumber(Math.abs(promena), locale, JEDNA)} %
    </span>
  );
}
